import { Box, Card, CardContent, Typography, Button, Chip, Divider } from '@mui/material';
import { Article, Search as SearchIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getEpisodeDetailRoute } from '@constants/routes';
import { highlightText } from '@utils/text/textUtils';
import { getEpisodeSearchSnippet } from '@models/search/searchModel';
import EmptyState from '@components/shared/EmptyState';

export default function SearchResults({ results, query }) {
  const navigate = useNavigate();

  if (results.length === 0) {
    return (
      <EmptyState
        type="no-results"
        message={`No episodes found for "${query}". Try different keywords.`}
      />
    );
  }

  return (
    <Box>
      {/* Results Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 1,
          mb: 3,
          minWidth: 0
        }}
      >
        <Chip
          icon={<SearchIcon />}
          label={`${results.length} result${results.length !== 1 ? 's' : ''}`}
          color="primary"
          sx={{ fontWeight: 600 }}
        />
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ minWidth: 0, overflowWrap: 'anywhere' }}
        >
          Searching all episodes for "{query}"
        </Typography>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Results List */}
      {results.map((result, index) => {
        const episode = result.item;
        const snippet = getEpisodeSearchSnippet(episode, query);
        const highlightedParts = highlightText(snippet, query);

        return (
          <Card
            key={episode.id}
            sx={{
              mb: 2,
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                boxShadow: 4,
                transform: 'translateY(-2px)'
              }
            }}
            onClick={() => navigate(getEpisodeDetailRoute(episode.id))}
            onKeyDown={(event) => {
              if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
                event.preventDefault();
                navigate(getEpisodeDetailRoute(episode.id));
              }
            }}
            role="link"
            tabIndex={0}
            aria-label={`View transcript for ${episode.title}`}
          >
            <CardContent>
              {/* Episode Title */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1, minWidth: 0 }}>
                <Article color="primary" fontSize="small" />
                <Typography
                  variant="h6"
                  component="h3"
                  sx={{
                    minWidth: 0,
                    overflowWrap: 'anywhere',
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    fontWeight: 600
                  }}
                >
                  {episode.title}
                </Typography>
              </Box>

              {/* Snippet with Highlighting */}
              <Typography
                variant="body2"
                component="div"
                sx={{
                  mb: 2,
                  lineHeight: 1.7,
                  color: 'text.secondary'
                }}
              >
                {highlightedParts.map((part, index) => (
                  part.isMatch ? (
                    <Box
                      key={index}
                      component="mark"
                      sx={{
                        bgcolor: 'primary.light',
                        color: 'primary.contrastText',
                        fontWeight: 700,
                        px: 0.5,
                        borderRadius: 0.5
                      }}
                    >
                      {part.text}
                    </Box>
                  ) : (
                    <span key={index}>{part.text}</span>
                  )
                ))}
              </Typography>

              {/* Action Button */}
              <Button
                variant="contained"
                size="small"
                startIcon={<Article />}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(getEpisodeDetailRoute(episode.id));
                }}
                sx={{ fontWeight: 600 }}
              >
                View Transcript
              </Button>
            </CardContent>
          </Card>
        );
      })}
    </Box>
  );
}
